<template>
  <div class="split-page">
    <!-- Active session guard: user already has a paid report -->
    <div v-if="hasActiveReport" class="active-session-guard">
      <p class="active-session-msg">{{ t('nav.activeSessionWarning') }}</p>
      <div class="active-session-actions">
        <button class="btn-back-to-report" @click="store.setStep('report')">{{ t('nav.backToReport') }}</button>
        <button class="btn-delete-session" @click="handleDeleteData">{{ t('nav.deleteData') }}</button>
      </div>
    </div>

    <!-- Brand selection -->
    <div v-if="!hasActiveReport" class="split-left oauth-brand-section">
      <BrandSelector
        :brands="vehicleData.brands"
        :selected-brand="selectedBrand"
        @select="handleBrandSelect"
      />
    </div>

    <!-- Connect panel -->
    <div v-if="!hasActiveReport" class="split-right">
      <div class="split-inner">
        <div>
          <h2 class="split-title">{{ t('oauth.title') }}</h2>
          <p class="split-subtitle">{{ t('oauth.subtitle') }}</p>
          <p class="split-note">{{ t('oauth.loadingNote') }}</p>
        </div>

        <button
          @click="handleConnectTesla"
          :disabled="store.isProcessing || !store.selectedBrandId"
          class="split-btn"
        >
          <div v-if="store.isProcessing" class="spinner"></div>
          <span v-if="store.isProcessing">{{ t('oauth.connecting') }}</span>
          <span v-else>{{ t('actions.connectTesla') }}</span>
        </button>

        <div v-if="store.error" class="split-error">
          {{ store.error }}
        </div>

        <div class="oauth-privacy">
          <p>{{ t('oauth.privacyPoint1') }}</p>
          <p>{{ t('oauth.privacyPoint2') }}</p>
          <p>{{ t('oauth.privacyPoint3') }}</p>
          <p>{{ t('oauth.privacyPoint4') }}</p>
          <p>{{ t('oauth.privacyPoint5') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppStore } from '@/stores/app';
import { trackEvent } from '@/utils/analytics';
import BrandSelector from '@/components/BrandSelector.vue';
import vehicleDataJson from '@/data/vehicles.json';
import type { Brand, VehicleData } from '@/data/vehicles.types';

const vehicleData = vehicleDataJson as VehicleData;

const { t } = useI18n();
const store = useAppStore();

const selectedBrand = computed<Brand | null>(() => {
  if (!store.selectedBrandId) return null;
  return vehicleData.brands.find(b => b.id === store.selectedBrandId) || null;
});

const hasActiveSession = computed(() => {
  if (!store.sessionExpiresAt) return false;
  return new Date(store.sessionExpiresAt).getTime() > Date.now();
});

const hasActiveReport = computed(() =>
  hasActiveSession.value &&
  Object.keys(store.vehicleReports).length > 0 &&
  !!store.paymentId
);

const handleDeleteData = () => {
  if (window.confirm(t('nav.deleteDataConfirm'))) {
    store.reset();
  }
};

const handleBrandSelect = (brand: Brand) => {
  store.setSelectedBrandId(brand.id);
};

/**
 * Initiate Tesla OAuth flow — redirects to Tesla's authorization page.
 * v2: sends brand only (no paymentId/plan — OAuth happens before payment).
 */
const handleConnectTesla = async () => {
  const brand = store.selectedBrandId;
  if (!brand) {
    store.setError('Please select a brand first.');
    store.setStep('landing');
    return;
  }

  // GA4 Tesla login start (spec 049-google-analytics, Task 3.4). No-op until consent.
  trackEvent('tesla_login_start', { brand });

  store.setProcessing(true);
  store.setError(null);

  try {
    const authUrl = await store.startTeslaOAuth(brand);

    if (!authUrl || typeof authUrl !== 'string') {
      throw new Error('Invalid OAuth URL received from server');
    }

    window.location.href = authUrl;
  } catch (error) {
    console.error('[OAuthStep] OAuth error:', error);
    const errorMessage = error instanceof Error ? error.message : 'Failed to initiate Tesla OAuth';
    store.setError(errorMessage);
    store.setProcessing(false);
  }
};
</script>

<style scoped>
.active-session-guard {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: calc(100vh - var(--nav-height));
  gap: 2rem;
  padding: 2rem;
  text-align: center;
}
.active-session-msg {
  color: rgba(255, 255, 255, 0.85);
  font-size: 1.05rem;
  max-width: 36rem;
  line-height: 1.6;
}
.active-session-actions {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;
}
.btn-back-to-report {
  background: #b8f400;
  color: #0d0f0d;
  font-weight: 700;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  font-size: 0.95rem;
  transition: opacity 0.15s;
}
.btn-back-to-report:hover { opacity: 0.88; }
.btn-delete-session {
  background: transparent;
  color: var(--color-error, #dc3545);
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  border: 1px solid var(--color-error, #dc3545);
  cursor: pointer;
  font-size: 0.95rem;
  transition: opacity 0.15s;
}
.btn-delete-session:hover { opacity: 0.75; }

/* BrandSelector dark-theme overrides */
.oauth-brand-section :deep(.split-panel-dark) { background: transparent; }
.oauth-brand-section :deep(.brand-item) { color: var(--white); }
.oauth-brand-section :deep(.brand-item-active) { background: rgba(255, 255, 255, 0.06); color: var(--white); }
.oauth-brand-section :deep(.brand-item-disabled) { color: rgba(255, 255, 255, 0.3); }
.oauth-brand-section :deep(.heading-hero) { color: var(--white); font-weight: 800; letter-spacing: -0.03em; }
.oauth-brand-section :deep(.label-section),
.oauth-brand-section :deep(.label-section-dark) { color: rgba(255, 255, 255, 0.45); font-size: 0.72rem; letter-spacing: 0.12em; }

.oauth-privacy p { color: rgba(255, 255, 255, 0.7); font-size: 0.75rem; line-height: 1.6; }

.spinner {
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid rgba(13, 15, 13, 0.3);
  border-top-color: #0d0f0d;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
